import { useCallback, useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { Utils } from "../../../utils";
import moment from "../../../utils/moment";
import { UserService } from "../../../services";
import {
  useBuyerCounts,
  useBuyerBidsYearly,
  useMyBidsList,
} from "../../../hooks/queries";
import { usePersistStore, useStore } from "../../../store";
import Table from "../../../components/Table";
import { Button } from "../../../controls";
import BidStatusDropdown from "../../../components/BidStatusDropdown";
import { Link, useNavigate } from "react-router-dom";
import { Skeleton } from "boneyard-js/react";
import { BidStatusChip } from "../../../components/status";
import StatCard from "../../../components/StatCard";
import TableSearchInput from "../../../components/TableSearchInput";
import { useResolvedTheme } from "../../../hooks/useTheme";

function Buyer() {
  const navigate = useNavigate();
  const { token, user, isBuyerProfileCompleted, setIsBuyerProfileCompleted } =
    usePersistStore((s: any) => s);
  const { data: counts } = useBuyerCounts(token);

  const checkIfProfileIsCompleted = useCallback(async () => {
    let result = await UserService.reload(token);
    if (result?.data) {
      let user = result?.data;
      if (
        user?.address1 &&
        user?.address2 &&
        user?.city &&
        user?.state &&
        user?.zip &&
        user?.name &&
        user?.phone &&
        user?.email
      ) {
        setIsBuyerProfileCompleted(true);
      } else {
        setIsBuyerProfileCompleted(false);
      }
    }
  }, [setIsBuyerProfileCompleted, token]);

  useEffect(() => {
    checkIfProfileIsCompleted();
  }, [checkIfProfileIsCompleted]);


  const BuyerYearlyBids = () => {
    const resolvedTheme = useResolvedTheme();
    const isDark = resolvedTheme === "dark";
    const gridBorder = isDark ? "#1f1f1f" : "#e0e0e0";
    const axisText = isDark ? "#888888" : "#6e6e6e";
    const options: ApexOptions = {
      plotOptions: {
        bar: {
          // % of each month's slot the grouped bars occupy. The remaining
          // space becomes the gap between adjacent month groups, so
          // dropping this from "60%" → "40%" gives ~50px breathing room
          // per month in a typical 12-month yearly chart.
          columnWidth: "40%",
          borderRadius: 6,
          borderRadiusApplication: "end",
        },
      },
      chart: {
        type: "bar",
        height: "100%",
        zoom: { enabled: false },
        toolbar: { show: false },
        foreColor: axisText,
        background: "transparent",
      },
      // Brand green for Total, warm sand for Winning — high-enough contrast
      // that colour-blind viewers can still read the grouped bars.
      colors: ["#51806F", "#D9A441"],
      dataLabels: { enabled: false },
      stroke: {
        curve: "smooth",
        lineCap: "round",
      },
      legend: {
        show: true,
        position: "top",
        horizontalAlign: "right",
        itemMargin: { horizontal: 12 },
      },
      xaxis: {
        categories: Array.apply(0, Array(12)).map(function (_, i) {
          return moment().month(i).format("MMM YYYY");
        }),
        labels: { show: true },
        axisBorder: { show: true },
      },
      yaxis: {
        forceNiceScale: true,
        labels: {
          show: true,
          formatter: (val) => Utils.formatToNumber(val.toString()),
        },
        axisBorder: { show: true },
      },
      grid: {
        show: true,
        borderColor: gridBorder,
        strokeDashArray: 0,
        position: "back",
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: false } },
      },
      tooltip: {
        theme: isDark ? "dark" : "light",
      },
      noData: {
        text: "No data available",
        align: "center",
        verticalAlign: "middle",
        offsetX: 0,
        offsetY: 0,
        style: {
          color: undefined,
          fontSize: "12px",
          fontFamily: undefined,
        },
      },
    };
    const { data: yearly } = useBuyerBidsYearly(token, moment().year());

    // Aligns the API's sparse month rows onto a fixed [Jan..Dec] array so
    // the two series have matching shape for grouped bars. Missing months
    // become zero.
    const toMonthlyTotals = (rows: any[] = []) => {
      const out = new Array(12).fill(0);
      for (const r of rows) {
        const m = parseInt(moment(r?._id).format("M"), 10);
        if (m >= 1 && m <= 12) out[m - 1] = r?.total || 0;
      }
      return out;
    };

    const data = [
      { name: "Total Bids", data: toMonthlyTotals(yearly?.all || []) },
      { name: "Winning Bids", data: toMonthlyTotals(yearly?.win || []) },
    ];

    return <Chart options={options} series={data} type="bar" height={330} />;
  };

  const BidsList = () => {
    const [status, setStatus] = useState("");
    const [keyword, setKeyword] = useState("");
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const { token } = usePersistStore((s: any) => s);
    const { setErrorMessage } = useStore((s: any) => s);

    const { data, isLoading, error } = useMyBidsList(token, {
      keyword,
      page,
      limit: rowsPerPage,
      status,
      sorting: "newest-first",
    });
    const list = data?.bids || [];
    const count = data?.count || 0;
    const isDone = !isLoading;

    useEffect(() => {
      if (error) setErrorMessage((error as Error).message);
    }, [error, setErrorMessage]);

    const renderPercentageChange = useCallback((row: any) => {
      return Utils.formatPercentChange(row?.adId?.price, row?.price, 1);
    }, []);

    const renderBadge = useCallback(
      (row: any) => <BidStatusChip status={row?.status} />,
      []
    );

    const columns = useMemo(
      () => [
        {
          title: "Artwork",
          thAlign: "left",
          tdAlign: "left",
          formatter: (row: any) => (
            <Link
              to={`/details/${row?.adId?._id}`}
              style={{ color: "var(--main-color)", textDecoration: "none" }}
            >
              {row?.adId?.title}
            </Link>
          ),
        },
        {
          title: "Opening Price",
          thAlign: "right",
          tdAlign: "right",
          formatter: (row: any) => Utils.formatToCurrency(row?.adId?.price),
        },
        {
          title: "Bidding Price",
          thAlign: "right",
          tdAlign: "right",
          formatter: (row: any) => Utils.formatToCurrency(row?.price),
        },
        {
          title: "% Increase",
          thAlign: "right",
          tdAlign: "right",
          formatter: (row: any) => renderPercentageChange(row),
        },
        {
          title: "Bid Placed",
          thAlign: "center",
          tdAlign: "center",
          formatter: (row: any) => Utils.formatDateTime(row?.createdAt),
        },
        {
          title: "Closes in",
          thAlign: "center",
          tdAlign: "center",
          formatter: (row: any) => Utils.formatFromNow(row?.adId?.biddingEnds),
        },
        {
          title: "Status",
          thAlign: "center",
          tdAlign: "center",
          formatter: (row: any) => renderBadge(row),
        },
      ],
      [renderPercentageChange, renderBadge]
    );

    const renderMobileCard = useCallback(
      (row: any) => (
        <div className="bidsCard">
          <div className="bidsCard__top">
            <Link
              to={`/details/${row?.adId?._id}`}
              className="bidsCard__title"
            >
              {row?.adId?.title}
            </Link>
            <div className="bidsCard__status">{renderBadge(row)}</div>
          </div>
          <div className="bidsCard__grid">
            <div className="bidsCard__stat">
              <span className="bidsCard__statLabel">Opening</span>
              <span className="bidsCard__statValue">
                {Utils.formatToCurrency(row?.adId?.price)}
              </span>
            </div>
            <div className="bidsCard__stat">
              <span className="bidsCard__statLabel">My bid</span>
              <span className="bidsCard__statValue">
                {Utils.formatToCurrency(row?.price)}
              </span>
            </div>
            <div className="bidsCard__stat">
              <span className="bidsCard__statLabel">Change</span>
              <span className="bidsCard__statValue">
                {renderPercentageChange(row)}
              </span>
            </div>
          </div>
          <div className="bidsCard__meta">
            <div className="bidsCard__metaRow">
              <i className="bi bi-clock" aria-hidden="true"></i>
              <span>Placed {Utils.formatDateTime(row?.createdAt)}</span>
            </div>
            <div className="bidsCard__metaRow">
              <i className="bi bi-hourglass-split" aria-hidden="true"></i>
              <span>
                Closes {Utils.formatFromNow(row?.adId?.biddingEnds)}
              </span>
            </div>
          </div>
        </div>
      ),
      [renderBadge, renderPercentageChange]
    );

    return (
      <div className="chartCard py-4">
        <div className="tableCardHeader px-4">
          <div className="tableCardHeader__titleBlock">
            <h2 className="tableCardHeader__title m-0">My Bids</h2>
            <p className="tableCardHeader__subtitle m-0">
              {isDone
                ? `${count} ${count === 1 ? "bid" : "bids"} total`
                : "Loading your bids…"}
            </p>
          </div>
          <div className="tableCardHeader__actions customTitle">
            <BidStatusDropdown value={status} onChange={setStatus} />
            <TableSearchInput value={keyword} onChange={setKeyword} />
          </div>
        </div>
        <Table
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          page={page}
          setPage={setPage}
          columns={columns}
          rows={list}
          isDone={isDone}
          count={count}
          mobileCard={renderMobileCard}
        />
      </div>
    );
  };

  return (
    <div className="bg-pink p-4">
      {!isBuyerProfileCompleted && (
        <div
          className="alert alert-warning d-flex justify-content-between align-items-center p-3"
          role="alert"
        >
          <div>
            Your profile is missing required fields. Please complete your
            profile to be able to bid on a artwork.
          </div>
          <Button
            title="My Profile"
            onClick={() => navigate("/dashboard/profile")}
            className="btn-warning"
          />
        </div>
      )}

      <div className="row g-3">
        <div className="col-6">
          {counts ? (
            <StatCard
              title={`${moment().format("MMM")}, bids placed`}
              value={Utils.formatToNumber(counts?.bids || 0)}
              icon="bi-hammer"
              tone="warning"
            />
          ) : (
            <Skeleton name="stat-card" loading={true}>
              <span style={{ display: "block", height: 80 }} />
            </Skeleton>
          )}
        </div>
        <div className="col-6">
          {counts ? (
            <StatCard
              title={`${moment().format("MMM")}, bids won`}
              value={Utils.formatToNumber(counts?.won || 0)}
              icon="bi-trophy-fill"
              tone="success"
            />
          ) : (
            <Skeleton name="stat-card" loading={true}>
              <span style={{ display: "block", height: 80 }} />
            </Skeleton>
          )}
        </div>
      </div>
      <div className="row mt-4">
        <div className="col-12">
          <div className="chartCard p-4">
            <div className="mb-3">
              <h5 className="mb-1">Bids Summary</h5>
              <p className="mb-0" style={{ opacity: "0.5" }}>
                Total vs winning bids, this year
              </p>
            </div>
            <BuyerYearlyBids />
          </div>
        </div>
      </div>
      <div className="row mt-4">
        <div className="col-12">
          <BidsList />
        </div>
      </div>
    </div>
  );
}

export default Buyer;
